
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';
import { getAccessToken, fetchYouTubeChannels, saveYouTubeProfile } from '@/services/youtube/authService';
import { syncYouTubeVideos, syncYouTubeComments } from '@/services/youtube/syncService';
import { YouTubeChannel } from '@/components/dashboard/profiles/types';

export function useYouTubeAuth() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [debugInfo, setDebugInfo] = useState('');
  
  // États pour la sélection de chaîne
  const [showChannelSelection, setShowChannelSelection] = useState(false);
  const [channels, setChannels] = useState<YouTubeChannel[]>([]);
  const [tokens, setTokens] = useState<{
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
  } | null>(null);
  const [userEmail, setUserEmail] = useState('');
  const [isSyncing, setIsSyncing] = useState(false);
  
  useEffect(() => {
    const handleAuth = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const code = searchParams.get('code');
        const state = searchParams.get('state') || '';
        
        if (!code) {
          setError('Aucun code d\'autorisation reçu.');
          setIsLoading(false);
          return;
        }
        
        setDebugInfo(`Code d'autorisation reçu: ${code.substring(0, 10)}...`);
        
        // Étape 1: Obtenir le token d'accès
        const tokenResponse = await getAccessToken(code, state);
        setDebugInfo(prev => `${prev}\nToken d'accès obtenu, expiration dans ${tokenResponse.expires_in} secondes`);
        
        // Sauvegarder les tokens pour une utilisation ultérieure
        setTokens({
          accessToken: tokenResponse.access_token,
          refreshToken: tokenResponse.refresh_token,
          expiresIn: tokenResponse.expires_in
        });
        
        // Étape 2: Récupérer les chaînes YouTube de l'utilisateur
        const channelsData = await fetchYouTubeChannels(tokenResponse.access_token);
        setDebugInfo(prev => `${prev}\n${channelsData.length} chaînes récupérées`);
        
        // Récupérer l'email de l'utilisateur connecté
        setUserEmail(localStorage.getItem('userEmail') || 'votre.email@gmail.com');
        
        // Stocker les chaînes et afficher l'interface de sélection
        setChannels(channelsData);
        setShowChannelSelection(true);
        setIsLoading(false);
        
      } catch (error: any) {
        console.error('Erreur lors de l\'authentification YouTube:', error);
        setError(error.message || 'Une erreur est survenue lors de l\'authentification.');
        setDebugInfo(prev => `${prev}\nErreur: ${error.message}`);
        setIsLoading(false);
      }
    };
    
    handleAuth();
  }, [searchParams, toast]);
  
  const handleSelectChannel = async (channel: YouTubeChannel) => {
    if (!tokens) {
      setError('Informations d\'authentification manquantes.');
      return;
    }
    
    try {
      setIsLoading(true);
      setDebugInfo(prev => `${prev}\nEnregistrement de la chaîne ${channel.title}...`);
      
      // Calculer l'expiration du token en secondes depuis l'époque Unix
      const tokenExpiry = Math.floor(Date.now() / 1000) + tokens.expiresIn;
      
      // Enregistrer le profil YouTube dans Supabase
      const savedProfile = await saveYouTubeProfile(
        channel.id,
        channel.title,
        channel.description || '',
        channel.thumbnailUrl,
        tokens.accessToken,
        tokens.refreshToken,
        tokenExpiry,
        channel.subscriberCount,
        channel.videoCount
      );
      
      setDebugInfo(prev => `${prev}\nChaîne enregistrée avec succès!`);
      
      // Afficher un message de succès
      toast({
        title: 'Chaîne YouTube connectée!',
        description: `Votre chaîne ${channel.title} a été connectée avec succès.`,
      });
      
      setSuccess(true);
      
      // Déclencher la synchronisation des vidéos YouTube, PUIS des commentaires
      if (savedProfile && savedProfile.id) {
        await syncChannelContent(savedProfile.id);
      }
      
      // Rediriger vers le tableau de bord après un court délai
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 2000);
      
    } catch (error: any) {
      console.error('Erreur lors de l\'enregistrement de la chaîne:', error);
      setError(error.message || 'Une erreur est survenue lors de l\'enregistrement de la chaîne.');
      setDebugInfo(prev => `${prev}\nErreur d'enregistrement: ${error.message}`);
      setIsLoading(false);
    }
  };
  
  const syncChannelContent = async (profileId: string) => {
    setIsSyncing(true);
    setDebugInfo(prev => `${prev}\nDémarrage de la synchronisation des vidéos...`);
    
    try {
      // Synchroniser les vidéos d'abord et ATTENDRE qu'elles soient terminées
      const videoResult = await syncYouTubeVideos(profileId);
      
      if (videoResult.success) {
        setDebugInfo(prev => `${prev}\nSynchronisation des vidéos réussie: ${videoResult.message}`);
        toast({
          title: 'Synchronisation des vidéos terminée',
          description: 'Vos vidéos ont été synchronisées avec succès.',
        });
        
        // Maintenant que les vidéos sont synchronisées, synchroniser les commentaires
        setDebugInfo(prev => `${prev}\nDémarrage de la synchronisation des commentaires...`);
        const commentsResult = await syncYouTubeComments(profileId);
        
        if (commentsResult.success) {
          setDebugInfo(prev => `${prev}\nSynchronisation des commentaires réussie: ${commentsResult.message}`);
          toast({
            title: 'Synchronisation terminée',
            description: 'Vos vidéos et commentaires ont été synchronisés avec succès.',
          });
        } else {
          setDebugInfo(prev => `${prev}\nErreur de synchronisation des commentaires: ${commentsResult.message}`);
          toast({
            title: 'Synchronisation partielle',
            description: 'Vos vidéos ont été importées, mais la synchronisation des commentaires a échoué.',
            variant: 'destructive',
          });
        }
      } else {
        setDebugInfo(prev => `${prev}\nErreur de synchronisation des vidéos: ${videoResult.message}`);
        toast({
          title: 'Erreur de synchronisation',
          description: 'Une erreur est survenue lors de la synchronisation des vidéos YouTube.',
          variant: 'destructive',
        });
      }
    } catch (syncError) {
      console.error('Erreur de synchronisation:', syncError);
      setDebugInfo(prev => `${prev}\nException lors de la synchronisation: ${syncError instanceof Error ? syncError.message : String(syncError)}`);
      toast({
        title: 'Erreur de synchronisation',
        description: 'Une erreur est survenue lors de la synchronisation des données YouTube.',
        variant: 'destructive',
      });
    } finally {
      setIsSyncing(false);
    }
  };
  
  const handleCancel = () => {
    navigate('/dashboard', { replace: true });
  };
  
  const handleRetry = () => {
    window.location.href = '/dashboard';
  };
  
  return {
    isLoading,
    error,
    success,
    debugInfo,
    showChannelSelection,
    channels,
    userEmail,
    isSyncing,
    handleSelectChannel,
    handleCancel,
    handleRetry
  };
}
